import { createApi } from '@reduxjs/toolkit/query/react';

import type { Blob as MatterBlob } from '../types/blob-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export interface EditorConfig {
  document_server_url: string;
  config: {
    document: {
      fileType: string;
      key: string;
      title: string;
      url: string;
    };
    editorConfig: {
      callbackUrl: string;
      user: { id: string; name: string };
      customization: Record<string, unknown>;
      mode: string;
    };
    documentType: string;
    token?: string;
  };
}

export interface ConvertedBlobResponse
  extends Pick<
    MatterBlob,
    | 'id'
    | 'name'
    | 'original_filename'
    | 'download_url'
    | 'file_size'
    | 'file_extension'
    | 'file_mime_type'
    | 'created_at'
    | 'modified_at'
    | 'collection_id'
  > {
  file_hash: string;
}

export const onlyofficeApi = createApi({
  reducerPath: 'onlyofficeApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['OnlyOffice'] as const,
  endpoints: (builder) => ({
    getBlobEditorConfig: builder.query<
      EditorConfig,
      { matterId: string; blobId: string; mode?: 'edit' | 'view' }
    >({
      query: ({ matterId, blobId, mode }) =>
        `api/matters/${matterId}/blobs/${blobId}/editor-config/${mode ? `?mode=${mode}` : ''}`,
    }),
    getTemplateEditorConfig: builder.query<
      EditorConfig,
      { firmId: string; templateId: string; mode?: 'edit' | 'view' }
    >({
      query: ({ firmId, templateId, mode }) =>
        `api/firms/${firmId}/templates/${templateId}/editor-config/${mode ? `?mode=${mode}` : ''}`,
    }),
    getDocumentEditorConfig: builder.query<
      EditorConfig,
      { matterId: string; documentId: string }
    >({
      query: ({ matterId, documentId }) =>
        `api/matters/${matterId}/documents/${documentId}/editor-config/`,
    }),
    getHipaaPackageComponentEditorConfig: builder.query<
      EditorConfig,
      {
        matterId: string;
        providerId: string;
        requestId: string;
        packageId: string;
        componentId: string;
      }
    >({
      query: ({
        matterId,
        providerId,
        requestId,
        packageId,
        componentId,
      }) =>
        `api/matters/${matterId}/providers/${providerId}/record-requests/${requestId}/package/${packageId}/components/${componentId}/editor-config/`,
    }),
    convertBlobToDocx: builder.mutation<
      ConvertedBlobResponse,
      { matterId: string; blobId: string }
    >({
      query: ({ matterId, blobId }) => ({
        url: `api/matters/${matterId}/blobs/${blobId}/convert-to-docx/`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetBlobEditorConfigQuery,
  useLazyGetBlobEditorConfigQuery,
  useGetTemplateEditorConfigQuery,
  useLazyGetTemplateEditorConfigQuery,
  useGetDocumentEditorConfigQuery,
  useLazyGetDocumentEditorConfigQuery,
  useGetHipaaPackageComponentEditorConfigQuery,
  useLazyGetHipaaPackageComponentEditorConfigQuery,
  useConvertBlobToDocxMutation,
} = onlyofficeApi;
